import React, { JSXElementConstructor, forwardRef, useEffect, useState } from 'react';

import { Card } from '../../../DataDisplay';
import { Image } from '../../../Deprecating/Image/Image';
import { Flex, Group, Stack } from '../../../Layout';
import { Box } from '../../../Miscellaneous/Box';
import { Collapse } from '../../../Miscellaneous/Collapse';
import { Text } from '../../../Typography';
import classes from './CollapsibleCard.module.css';

interface CollapsibleCardBaseProps {
  backgroundColor?: string;
  headerTitle?: React.ReactNode;
  headerElement?: JSX.Element;
  isCardExpanded?: boolean;
  endAdornment?: JSX.Element | React.ReactNode;
  children?: JSX.Element | JSX.Element[] | React.ReactNode;
  subtitle?: string;
  headerContent?: JSX.Element | React.ReactNode;
  ref?: React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
}

interface CollapsibleCardWithHeaderElementProps extends CollapsibleCardBaseProps {
  headerElement: JSX.Element;
  headerTitle?: never;
}

interface CollapsibleCardWithTitleProps extends CollapsibleCardBaseProps {
  headerElement?: never;
  headerNumber?: string | number;
  headerTitle?: React.ReactNode;
  endAdornment?: JSX.Element | React.ReactNode;
}

type CollapsibleCardProps = CollapsibleCardWithHeaderElementProps | CollapsibleCardWithTitleProps;

const _CollapsibleCard = forwardRef<HTMLDivElement, CollapsibleCardProps>(
  (
    {
      backgroundColor,
      headerTitle,
      headerElement,
      isCardExpanded = true,
      endAdornment,
      style,
      children,
      subtitle,
      headerContent,
    },
    ref
  ) => {
    const [isCollapseOpened, setIsCollapseOpened] = useState(false);

    useEffect(() => {
      setIsCollapseOpened(isCardExpanded);
    }, [isCardExpanded]);

    return (
      <Card bg={backgroundColor} ref={ref} w={'100%'} style={style} p="lg">
        <Card.Section
          px={{ base: 'md', sm: 'lg' }}
          pb={{ base: 'md', sm: 'lg' }}
          className={classes.cardSection}
          mod={{
            // DESIGN: Remove this mod from the component
            'show-border': false,
            'with-title': Boolean(headerTitle),
            'with-header': Boolean(headerElement),
            expanded: isCardExpanded,
          }}
        >
          <Stack gap="xs">
            {!headerElement && (
              <Flex fz={{ base: 'md', sm: '20px' }} align={'center'}>
                <Box w="100%">
                  <Flex w="100%" justify={'space-between'} align={'center'}>
                    <Text fw={600} fz={{ base: 'md', sm: '20px' }}>
                      {headerTitle}
                    </Text>
                    {endAdornment}
                  </Flex>
                  <Text fz="xs">{subtitle}</Text>
                </Box>
              </Flex>
            )}
            {headerElement}
            {headerContent}
          </Stack>
        </Card.Section>
        {isCardExpanded && (
          <Collapse in={isCollapseOpened} transitionDuration={350} animateOpacity>
            {children}
          </Collapse>
        )}
      </Card>
    );
  }
) as any;

const Header = ({
  imageUrl,
  title,
  subtitle,
  headerContent,
}: {
  imageUrl?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  headerContent?: JSX.Element;
}) => {
  return (
    <Group wrap="nowrap">
      {imageUrl !== undefined && (
        <Box
          style={(theme) => ({
            borderRadius: theme.radius.sm,
            overflow: 'hidden',
            minWidth: '48px',
          })}
        >
          <Image src={imageUrl} style={{ width: '48px', height: '48px' }} />
        </Box>
      )}

      <Stack gap={8}>
        {title}
        {subtitle && <Text c="white">{subtitle}</Text>}
      </Stack>
      {headerContent}
    </Group>
  );
};

_CollapsibleCard.displayName = 'CollapsibleCardBase';
_CollapsibleCard.Header = Header;

type _C = {
  Header: typeof Header;
};

export const CollapsibleCard = _CollapsibleCard as JSXElementConstructor<CollapsibleCardProps> & _C;
